import * as i0 from '@angular/core';
import { Injectable, Pipe } from '@angular/core';
import { isEmpty, forEach, isNumber, reduce, isObject } from 'lodash';
import { catchError, shareReplay } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import * as i2 from '@helix/platform/shared/api';
import { RX_BUNDLE, DataPage } from '@helix/platform/shared/api';
import { of } from 'rxjs';
import * as i2$1 from '@helix/platform/utils';

class RxDocumentDefinitionService {
    constructor(errorHandler, httpClient, rxCommandFactoryService) {
        this.errorHandler = errorHandler;
        this.httpClient = httpClient;
        this.rxCommandFactoryService = rxCommandFactoryService;
    }
    find(documentDefinitionName, options) {
        return documentDefinitionName
            ? this.get(documentDefinitionName, options).pipe(catchError((error) => {
                this.errorHandler.handleError(error);
                return of(null);
            }))
            : of(null);
    }
    get(documentDefinitionName, options) {
        return this.httpClient.get(this.getUrl(documentDefinitionName), options);
    }
    getNew() {
        return of({
            allowOverlay: false,
            documentSchema: null,
            name: null,
            scope: RX_BUNDLE.definitionScopeTypes.bundle
        });
    }
    create(documentDefinition) {
        return this.httpClient.post(this.getUrl(), documentDefinition, { observe: 'response' });
    }
    update(documentDefinition, options) {
        return this.httpClient.put(this.getUrl(documentDefinition.name), documentDefinition, options);
    }
    delete(documentDefinitionName) {
        return this.httpClient.delete(this.getUrl(documentDefinitionName));
    }
    revertCustomization(documentDefinitionName) {
        const revertCustomizationCommand = this.rxCommandFactoryService.forResourceType('com.bmc.arsys.rx.application.document.command.RevertDocumentDefinitionCommand');
        return revertCustomizationCommand.execute({ documentDefinitionName });
    }
    rename(oldDocumentDefinitionName, newDocumentDefinitionName) {
        const renameCommand = this.rxCommandFactoryService.forResourceType('com.bmc.arsys.rx.application.document.command.RenameDocumentDefinitionCommand');
        return renameCommand.execute({
            name: oldDocumentDefinitionName,
            newName: newDocumentDefinitionName
        });
    }
    getUrl(documentDefinitionName) {
        return documentDefinitionName
            ? `/api/rx/application/document/documentdefinition/${encodeURIComponent(documentDefinitionName)}`
            : '/api/rx/application/document/documentdefinition';
    }
}
RxDocumentDefinitionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentDefinitionService, deps: [{ token: i0.ErrorHandler }, { token: i1.HttpClient }, { token: i2.RxCommandFactoryService }], target: i0.ɵɵFactoryTarget.Injectable });
RxDocumentDefinitionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentDefinitionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentDefinitionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.ErrorHandler }, { type: i1.HttpClient }, { type: i2.RxCommandFactoryService }]; } });

class RxDocumentDefinitionCacheService {
    constructor(rxDocumentDefinitionService) {
        this.rxDocumentDefinitionService = rxDocumentDefinitionService;
        this.consumers = new Set();
        this.documentDefinitionCache = new Map();
    }
    getDocumentDefinition(documentDefinitionName) {
        if (!this.documentDefinitionCache.has(documentDefinitionName)) {
            const documentDefinition$ = this.rxDocumentDefinitionService.get(documentDefinitionName).pipe(shareReplay(1));
            this.documentDefinitionCache.set(documentDefinitionName, documentDefinition$);
        }
        return this.documentDefinitionCache.get(documentDefinitionName);
    }
    registerConsumer(consumerDestroy$) {
        this.consumers.add(consumerDestroy$);
        consumerDestroy$.subscribe(() => {
            this.consumers.delete(consumerDestroy$);
            if (isEmpty(this.consumers)) {
                this.clearCache();
            }
        });
    }
    clearCache(documentDefinitionNames) {
        if (documentDefinitionNames) {
            forEach(documentDefinitionNames, (documentDefinitionName) => {
                this.documentDefinitionCache.delete(documentDefinitionName);
            });
        }
        else {
            this.documentDefinitionCache.clear();
        }
    }
}
RxDocumentDefinitionCacheService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentDefinitionCacheService, deps: [{ token: RxDocumentDefinitionService }], target: i0.ɵɵFactoryTarget.Injectable });
RxDocumentDefinitionCacheService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentDefinitionCacheService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentDefinitionCacheService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: RxDocumentDefinitionService }]; } });

class RxDocumentDefinitionDataPageService extends DataPage {
    constructor(injector) {
        super(injector, 'com.bmc.arsys.rx.application.document.datapage.DocumentDefinitionDataPageQuery');
        this.injector = injector;
    }
}
RxDocumentDefinitionDataPageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentDefinitionDataPageService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
RxDocumentDefinitionDataPageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentDefinitionDataPageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentDefinitionDataPageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

class RxDocumentService {
    constructor() {
        this.SLASH = '/';
        this.SLASH_PLACEHOLDER = 'RX_SLASH';
        this.slashRegExp = new RegExp(this.SLASH, 'g');
        this.slashPlaceholderRegExp = new RegExp(this.SLASH_PLACEHOLDER, 'g');
    }
    encodeTreeNodePath(path) {
        return path.replace(this.slashRegExp, this.SLASH_PLACEHOLDER);
    }
    decodeTreeNodePath(path) {
        return path.replace(this.slashPlaceholderRegExp, this.SLASH);
    }
}
RxDocumentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
RxDocumentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxDocumentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class RxJsonToTreeConverterService {
    constructor(rxDocumentService, rxGuidService, rxJsonParserService) {
        this.rxDocumentService = rxDocumentService;
        this.rxGuidService = rxGuidService;
        this.rxJsonParserService = rxJsonParserService;
    }
    isArrayInArray(value, key) {
        return Array.isArray(value) && isNumber(key);
    }
    getFieldsFromJson(documentSchema) {
        return this.getFields(this.rxJsonParserService.tryParseJson(documentSchema, {}));
    }
    getFields(jsonObject, property) {
        return reduce(jsonObject, (result, value, key) => {
            // Skip building fields if json is array of arrays
            if (!this.isArrayInArray(value, key)) {
                // To avoid potential problems with subsequent node manipulations,
                // convert the value to a string if the key is a number (indicating that the entry is an array)
                const keyAsString = String(key);
                const node = {
                    label: keyAsString,
                    key: this.rxGuidService.generate(),
                    expanded: true,
                    children: [],
                    data: {}
                };
                const nodeKey = this.rxDocumentService.encodeTreeNodePath(keyAsString);
                if (property !== undefined) {
                    node.data.name = property + '.' + nodeKey;
                }
                else {
                    node.data.name = nodeKey;
                }
                if (Array.isArray(value)) {
                    node.children = [];
                    forEach(value, (key, index) => {
                        // Skip building fields if an array in json includes another array
                        if (!this.isArrayInArray(key, index)) {
                            const childNode = {
                                label: index.toString(),
                                key: this.rxGuidService.generate(),
                                children: [],
                                data: {
                                    name: node.data.name + '[' + index + ']'
                                }
                            };
                            if (isObject(value[index])) {
                                childNode.children = this.getFields(value[index], node.data.name + '[' + index + ']');
                            }
                            else {
                                childNode.label = value[index];
                            }
                            node.children.push(childNode);
                        }
                    });
                }
                else if (isObject(value)) {
                    node.children = this.getFields(value, node.data.name);
                }
                result.push(node);
            }
            return result;
        }, []);
    }
}
RxJsonToTreeConverterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxJsonToTreeConverterService, deps: [{ token: RxDocumentService }, { token: i2$1.RxGuidService }, { token: i2$1.RxJsonParserService }], target: i0.ɵɵFactoryTarget.Injectable });
RxJsonToTreeConverterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxJsonToTreeConverterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxJsonToTreeConverterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: RxDocumentService }, { type: i2$1.RxGuidService }, { type: i2$1.RxJsonParserService }]; } });

class RxTreeNodePathPipe {
    constructor(rxDocumentService) {
        this.rxDocumentService = rxDocumentService;
    }
    transform(value, action) {
        const path = value || '';
        if (!action || action === 'decode') {
            return this.rxDocumentService.decodeTreeNodePath(path);
        }
        else if (action === 'encode') {
            return this.rxDocumentService.encodeTreeNodePath(path);
        }
        else {
            return path;
        }
    }
}
RxTreeNodePathPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxTreeNodePathPipe, deps: [{ token: RxDocumentService }], target: i0.ɵɵFactoryTarget.Pipe });
RxTreeNodePathPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: RxTreeNodePathPipe, isStandalone: true, name: "rxTreeNodePathPipe" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxTreeNodePathPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'rxTreeNodePathPipe',
                    standalone: true
                }]
        }], ctorParameters: function () { return [{ type: RxDocumentService }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { RxDocumentDefinitionCacheService, RxDocumentDefinitionDataPageService, RxDocumentDefinitionService, RxDocumentService, RxJsonToTreeConverterService, RxTreeNodePathPipe };
